import type { GetCommentsByConversationIdResponseRow } from '@risksmart-app/trpc/src/types';
import type { GetCommentsByConversationIdQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { GetCommentsByConversationIdDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useCallback } from 'react';
import { createLazyQueryHook } from 'src/utils';

type CommentsByConversationIdArgs = { conversationId: string };

/**
 * Imperative (lazy) read of the comments for one conversation — RSP-6214.
 * The editor's tinycomments integration (`useComments.tinycomments_lookup`)
 * calls this on demand and awaits a fresh (no-cache) result.
 *
 * Built on the shared `createLazyQueryHook` factory (RSP-6655): the raw Apollo
 * `useLazyQuery` now lives inside the factory. The factory maps the tRPC rows
 * back to the GraphQL `comment` row shape so the downstream tinycomments
 * mapping stays unchanged; the GraphQL branch uses `no-cache`.
 *
 * The tinycomments `fail(e)` callback needs a THROW on error, but the factory
 * swallows errors (global notification + empty data). `getComments` therefore
 * RE-THROWS `result.error` so `fail(e)` still fires. Trade-off accepted in
 * RSP-6655: an error now ALSO surfaces a global notification in addition to the
 * inline tinycomments failure.
 */
const useLazyCommentsByConversationId = createLazyQueryHook<
  CommentsByConversationIdArgs,
  GetCommentsByConversationIdResponseRow[],
  GetCommentsByConversationIdQuery
>({
  trpcQuery: (client, { conversationId }) =>
    client.frontend.comment.commentsByConversationId.query({ conversationId }),
  mapTrpcDataToGraphQL: (rows) => ({
    comment: rows.map((row) => ({
      Id: row.Id,
      Content: row.Content,
      ModifiedAtTimestamp: row.ModifiedAtTimestamp,
      CreatedAtTimestamp: row.CreatedAtTimestamp,
      CreatedByUser: row.CreatedByUser,
      ModifiedByUser: row.ModifiedByUser,
      createdByUser: row.createdByUser
        ? { FriendlyName: row.createdByUser.FriendlyName }
        : null,
      modifiedByUser: row.modifiedByUser
        ? { FriendlyName: row.modifiedByUser.FriendlyName }
        : null,
    })),
  }),
  graphqlDocument: GetCommentsByConversationIdDocument,
  graphqlVariables: ({ conversationId }) => ({
    ConversationId: conversationId,
  }),
  graphqlFetchPolicy: 'no-cache',
});

export const useGetCommentsByConversationId = () => {
  const [fetchCommentsByConversationId] = useLazyCommentsByConversationId();

  const getComments = useCallback(
    async (
      conversationId: string
    ): Promise<GetCommentsByConversationIdQuery['comment']> => {
      const result = await fetchCommentsByConversationId({ conversationId });
      if (result.error) {
        throw result.error;
      }

      return result.data?.comment ?? [];
    },
    [fetchCommentsByConversationId]
  );

  return { getComments };
};
